import type { Role } from './types';

/**
 * Transitions. Every entry maps to an ffmpeg `xfade` transition (shape wipes to
 * its custom expression, see wipes.ts) so that
 * the browser preview (GLSL in apps/web/src/engine/transitions.ts) and the
 * server export render the same effect.
 */
export const TRANSITIONS = [
  { id: 'fade', label: 'Cross dissolve', xfade: 'fade' },
  { id: 'fadeblack', label: 'Dip to black', xfade: 'fadeblack' },
  { id: 'fadewhite', label: 'Dip to white', xfade: 'fadewhite' },
  { id: 'dissolve', label: 'Noise dissolve', xfade: 'dissolve' },
  { id: 'wipeleft', label: 'Wipe left', xfade: 'wipeleft' },
  { id: 'wiperight', label: 'Wipe right', xfade: 'wiperight' },
  { id: 'wipeup', label: 'Wipe up', xfade: 'wipeup' },
  { id: 'wipedown', label: 'Wipe down', xfade: 'wipedown' },
  { id: 'slideleft', label: 'Slide left', xfade: 'slideleft' },
  { id: 'slideright', label: 'Slide right', xfade: 'slideright' },
  { id: 'slideup', label: 'Slide up', xfade: 'slideup' },
  { id: 'slidedown', label: 'Slide down', xfade: 'slidedown' },
  { id: 'smoothleft', label: 'Smooth left', xfade: 'smoothleft' },
  { id: 'smoothright', label: 'Smooth right', xfade: 'smoothright' },
  { id: 'circleopen', label: 'Circle open', xfade: 'circleopen' },
  { id: 'circleclose', label: 'Circle close', xfade: 'circleclose' },
  { id: 'radial', label: 'Radial', xfade: 'radial' },
  { id: 'pixelize', label: 'Pixelize', xfade: 'pixelize' },
  { id: 'zoomin', label: 'Zoom in', xfade: 'zoomin' },
  { id: 'hblur', label: 'Blur', xfade: 'hblur' },
  // Shape wipes (wipes.ts): one field-wipe shader in the preview, xfade's custom expression in the export.
  { id: 'irisround', label: 'Iris round', xfade: 'custom' },
  { id: 'irisbox', label: 'Iris box', xfade: 'custom' },
  { id: 'irisdiamond', label: 'Iris diamond', xfade: 'custom' },
  { id: 'star', label: 'Star', xfade: 'custom' },
  { id: 'heart', label: 'Heart', xfade: 'custom' },
  { id: 'triangle', label: 'Triangle', xfade: 'custom' },
  { id: 'hexagon', label: 'Hexagon', xfade: 'custom' },
  { id: 'clockwipe', label: 'Clock wipe', xfade: 'custom' },
  { id: 'barndoorv', label: 'Barn door (vertical)', xfade: 'custom' },
  { id: 'barndoorh', label: 'Barn door (horizontal)', xfade: 'custom' },
] as const;

export type TransitionId = (typeof TRANSITIONS)[number]['id'];
export const TRANSITION_IDS: readonly string[] = TRANSITIONS.map((t) => t.id);

/**
 * Look presets. Each is a 3x3 RGB matrix (row-major, rows produce R,G,B)
 * plus optional eq-style adjustments, blended with identity by intensity.
 * Maps to ffmpeg `colorchannelmixer` + `eq`, and to a GLSL mat3 in preview.
 */
export interface FilterPreset {
  id: string;
  label: string;
  matrix?: readonly number[];
  contrast?: number;
  saturation?: number;
  brightness?: number;
}

const LUMA = [0.299, 0.587, 0.114];

export const FILTER_PRESETS: readonly FilterPreset[] = [
  { id: 'mono', label: 'Mono', matrix: [...LUMA, ...LUMA, ...LUMA] },
  { id: 'noir', label: 'Noir', matrix: [...LUMA, ...LUMA, ...LUMA], contrast: 1.45, brightness: -0.04 },
  { id: 'sepia', label: 'Sepia', matrix: [0.393, 0.769, 0.189, 0.349, 0.686, 0.168, 0.272, 0.534, 0.131] },
  { id: 'vintage', label: 'Vintage', matrix: [0.9, 0.2, 0.0, 0.1, 0.8, 0.1, 0.1, 0.2, 0.6], contrast: 0.9, saturation: 0.8 },
  { id: 'warm', label: 'Warm', matrix: [1.1, 0.0, 0.0, 0.0, 1.0, 0.0, 0.0, 0.0, 0.85] },
  { id: 'cool', label: 'Cool', matrix: [0.88, 0.0, 0.0, 0.0, 1.0, 0.0, 0.0, 0.0, 1.12] },
  { id: 'vivid', label: 'Vivid', saturation: 1.5, contrast: 1.1 },
  { id: 'muted', label: 'Muted', saturation: 0.55, contrast: 0.92 },
  { id: 'dramatic', label: 'Dramatic', contrast: 1.35, saturation: 0.85, brightness: -0.03 },
  { id: 'tealorange', label: 'Teal & Orange', matrix: [1.08, 0.0, -0.05, -0.02, 1.0, 0.02, -0.05, 0.05, 1.05], saturation: 1.15 },
];

export const FILTER_PRESET_IDS: readonly string[] = FILTER_PRESETS.map((f) => f.id);

export function findFilterPreset(id: string): FilterPreset | undefined {
  return FILTER_PRESETS.find((f) => f.id === id);
}

/**
 * Properties that can be keyframed, with their value ranges and the group
 * the editor lists them under. Properties without a static clip field
 * (tilt, text reveal) rest at `base` when they have no keyframes.
 */
export const KEYFRAME_PROPERTIES = {
  x: { label: 'Position X', min: -20000, max: 20000, step: 1, group: 'transform' },
  y: { label: 'Position Y', min: -20000, max: 20000, step: 1, group: 'transform' },
  scale: { label: 'Scale', min: 0, max: 20, step: 0.01, group: 'transform' },
  rotation: { label: 'Rotation', min: -3600, max: 3600, step: 0.1, group: 'transform' },
  opacity: { label: 'Opacity', min: 0, max: 1, step: 0.01, group: 'transform' },
  tiltX: { label: 'Tilt X (3D)', min: -80, max: 80, step: 0.1, group: '3d', base: 0 },
  tiltY: { label: 'Tilt Y (3D)', min: -80, max: 80, step: 0.1, group: '3d', base: 0 },
  cropLeft: { label: 'Crop left', min: 0, max: 0.95, step: 0.001, group: 'crop' },
  cropRight: { label: 'Crop right', min: 0, max: 0.95, step: 0.001, group: 'crop' },
  cropTop: { label: 'Crop top', min: 0, max: 0.95, step: 0.001, group: 'crop' },
  cropBottom: { label: 'Crop bottom', min: 0, max: 0.95, step: 0.001, group: 'crop' },
  volume: { label: 'Volume', min: 0, max: 4, step: 0.01, group: 'audio' },
  speed: { label: 'Speed (ramp)', min: 0.1, max: 16, step: 0.01, group: 'time' },
  brightness: { label: 'Brightness', min: -1, max: 1, step: 0.01, group: 'color' },
  contrast: { label: 'Contrast', min: 0, max: 3, step: 0.01, group: 'color' },
  saturation: { label: 'Saturation', min: 0, max: 3, step: 0.01, group: 'color' },
  gamma: { label: 'Gamma', min: 0.1, max: 3, step: 0.01, group: 'color' },
  hue: { label: 'Hue', min: -180, max: 180, step: 0.5, group: 'color' },
  temperature: { label: 'Temperature', min: 2000, max: 12000, step: 50, group: 'color' },
  tint: { label: 'Tint', min: -1, max: 1, step: 0.01, group: 'color' },
  pivot: { label: 'Contrast pivot', min: 0, max: 1, step: 0.01, group: 'color', base: 0.5 },
  liftMaster: { label: 'Lift (master)', min: -1, max: 1, step: 0.005, group: 'color' },
  gammaMaster: { label: 'Gamma (master)', min: -1, max: 1, step: 0.005, group: 'color' },
  gainMaster: { label: 'Gain (master)', min: -2, max: 2, step: 0.01, group: 'color' },
  offsetMaster: { label: 'Offset (master)', min: -1, max: 1, step: 0.005, group: 'color' },
  shadows: { label: 'Shadows', min: -1, max: 1, step: 0.01, group: 'color' },
  highlights: { label: 'Highlights', min: -1, max: 1, step: 0.01, group: 'color' },
  colorBoost: { label: 'Color boost', min: -1, max: 1, step: 0.01, group: 'color' },
  filterIntensity: { label: 'Look intensity', min: 0, max: 1, step: 0.01, group: 'effects' },
  blur: { label: 'Blur', min: 0, max: 200, step: 0.1, group: 'effects' },
  vignette: { label: 'Vignette', min: 0, max: 1, step: 0.01, group: 'effects' },
  grain: { label: 'Film grain', min: 0, max: 1, step: 0.01, group: 'effects' },
  glow: { label: 'Glow', min: 0, max: 1, step: 0.01, group: 'effects' },
  aberration: { label: 'Chromatic aberration', min: 0, max: 1, step: 0.01, group: 'effects' },
  lensDistortion: { label: 'Lens distortion', min: -1, max: 1, step: 0.01, group: 'effects' },
  revealChars: { label: 'Typewriter (letters)', min: 0, max: 1, step: 0.001, group: 'text', base: 1 },
  revealWords: { label: 'Reveal words', min: 0, max: 1, step: 0.001, group: 'text', base: 1 },
} as const satisfies Record<string, { label: string; min: number; max: number; step: number; group: KeyframeGroup; base?: number }>;

export type KeyframeGroup = 'transform' | '3d' | 'crop' | 'audio' | 'color' | 'effects' | 'text' | 'time';

export const KEYFRAME_GROUPS: { id: KeyframeGroup; label: string }[] = [
  { id: 'transform', label: 'Transform' },
  { id: '3d', label: '3D' },
  { id: 'crop', label: 'Crop' },
  { id: 'color', label: 'Color' },
  { id: 'effects', label: 'Effects' },
  { id: 'text', label: 'Text' },
  { id: 'audio', label: 'Audio' },
  { id: 'time', label: 'Time' },
];

/** Whether a property applies to a clip kind (e.g. text reveal only to text). */
export function propertyAppliesTo(property: string, clipKind: string): boolean {
  const def = keyframeDef(property);
  if (!def) return false;
  if (def.group === 'audio' || def.group === 'time') return clipKind === 'audio' || clipKind === 'video';
  if (clipKind === 'audio') return false;
  if (def.group === 'text') return clipKind === 'text';
  if (def.group === 'crop') return clipKind === 'video' || clipKind === 'image' || clipKind === 'shape';
  if (clipKind === 'adjustment') return def.group === 'color' || def.group === 'effects' || property === 'opacity';
  return true;
}

export type KeyframeProperty = keyof typeof KEYFRAME_PROPERTIES;
export const KEYFRAME_PROPERTY_IDS = Object.keys(KEYFRAME_PROPERTIES) as KeyframeProperty[];

/**
 * Grade properties every colour node can animate. On the first node they are
 * stored under their plain name (as single grades always were); on any other
 * node as `<property>@<node id>`, e.g. `liftMaster@n3`.
 */
export const NODE_KEYFRAME_PROPERTIES = ['liftMaster', 'gammaMaster', 'gainMaster', 'offsetMaster', 'tint', 'shadows', 'highlights', 'colorBoost'] as const;
const NODE_PROP = /^([A-Za-z]+)@([A-Za-z0-9_-]{1,24})$/;

/** The base property and node id of a node-scoped property (`liftMaster@n3`); node is undefined for plain names. */
export function splitNodeProperty(property: string): { base: string; node?: string } {
  const m = NODE_PROP.exec(property);
  return m && (NODE_KEYFRAME_PROPERTIES as readonly string[]).includes(m[1]) ? { base: m[1], node: m[2] } : { base: property };
}

/** The keyframe property of a node's grade property (`first`: the node is the clip's first, which keeps plain names). */
export const nodeProperty = (base: string, nodeId: string, first: boolean) => (first ? base : `${base}@${nodeId}`);

/** The catalog entry of a keyframe property, node-scoped grade properties included. */
export function keyframeDef(property: string): (typeof KEYFRAME_PROPERTIES)[KeyframeProperty] | undefined {
  const { base } = splitNodeProperty(property);
  return KEYFRAME_PROPERTIES[base as KeyframeProperty];
}

export const isKeyframeProperty = (property: string) => keyframeDef(property) !== undefined;


/** Roles that give access to the whole organization (guests are invited to a space or folder instead). */
export const ROLES: readonly Role[] = ['owner', 'admin', 'editor', 'viewer'];
export const ROLE_RANK: Record<Role, number> = { owner: 4, admin: 3, editor: 2, viewer: 1, guest: 0 };

export function roleAtLeast(role: string | undefined | null, min: Role): boolean {
  if (!role) return false;
  const rank = ROLE_RANK[role as Role];
  return rank !== undefined && rank >= ROLE_RANK[min] && rank > 0;
}

/** Access inside a space or folder: grants give viewer or editor; owners and admins have admin everywhere. */
export type AccessRole = 'viewer' | 'editor' | 'admin';
export const ACCESS_ROLES: readonly ('viewer' | 'editor')[] = ['viewer', 'editor'];
const ACCESS_RANK: Record<AccessRole, number> = { viewer: 1, editor: 2, admin: 3 };

/** What an organization role means inside an open space (guests: nothing). */
export function accessFromOrgRole(role: string): AccessRole | null {
  return role === 'owner' || role === 'admin' ? 'admin' : role === 'editor' ? 'editor' : role === 'viewer' ? 'viewer' : null;
}

export function accessAtLeast(role: AccessRole | null | undefined, min: AccessRole): boolean {
  return !!role && ACCESS_RANK[role] >= ACCESS_RANK[min];
}

/**
 * A member's access in a scope ('' = open). Owners and admins see
 * everything. In a restricted scope, others need a grant (theirs or a
 * group's); members never get more than their organization role there.
 */
export function scopeRoleFor(orgRole: string, scopeId: string, granted: 'viewer' | 'editor' | null): AccessRole | null {
  const org = accessFromOrgRole(orgRole);
  if (org === 'admin') return 'admin';
  if (!scopeId) return org;
  if (!granted) return null;
  if (!org) return granted;
  return ACCESS_RANK[granted] <= ACCESS_RANK[org] ? granted : org;
}

/** The access role a min organization role stands for (for checks shared by open and restricted content). */
export const accessForMin = (min: Role): AccessRole => (min === 'owner' || min === 'admin' ? 'admin' : min === 'editor' ? 'editor' : 'viewer');

export const EXPORT_FORMATS = [
  { id: 'mp4_h264', label: 'MP4 (H.264)', ext: 'mp4', mime: 'video/mp4' },
  { id: 'mp4_h265', label: 'MP4 (H.265 / HEVC)', ext: 'mp4', mime: 'video/mp4' },
  { id: 'mov_prores', label: 'MOV (ProRes 422 HQ)', ext: 'mov', mime: 'video/quicktime' },
  { id: 'webm_vp9', label: 'WebM (VP9)', ext: 'webm', mime: 'video/webm' },
] as const;

export const EXPORT_RESOLUTIONS = [
  { label: '4K UHD', width: 3840, height: 2160 },
  { label: '1440p', width: 2560, height: 1440 },
  { label: '1080p', width: 1920, height: 1080 },
  { label: '720p', width: 1280, height: 720 },
  { label: '480p', width: 854, height: 480 },
] as const;

export const PROJECT_PRESETS = [
  { label: '1080p Landscape (16:9)', width: 1920, height: 1080 },
  { label: '4K Landscape (16:9)', width: 3840, height: 2160 },
  { label: '720p Landscape (16:9)', width: 1280, height: 720 },
  { label: 'Vertical 1080×1920 (9:16)', width: 1080, height: 1920 },
  { label: 'Square 1080×1080 (1:1)', width: 1080, height: 1080 },
  { label: 'Portrait 1080×1350 (4:5)', width: 1080, height: 1350 },
] as const;

export const FPS_OPTIONS = [23.976, 24, 25, 29.97, 30, 50, 59.94, 60] as const;

/** Fonts bundled with the web app and the worker (see assets/fonts). */
export const FONTS = [
  // Sans serif
  'Inter',
  'Montserrat',
  'Poppins',
  'Open Sans',
  'Lato',
  'Raleway',
  'Space Grotesk',
  // Display
  'Bebas Neue',
  'Oswald',
  'Anton',
  'Archivo Black',
  'Abril Fatface',
  // Serif
  'Playfair Display',
  'Merriweather',
  'Lora',
  'Roboto Slab',
  // Handwriting & script
  'Permanent Marker',
  'Dancing Script',
  'Caveat',
  'Pacifico',
  'Lobster',
  // Monospace & pixel
  'JetBrains Mono',
  'Press Start 2P',
] as const;

export const PRESENCE_COLORS = [
  '#f97316', '#22c55e', '#3b82f6', '#e11d48', '#a855f7', '#14b8a6', '#eab308', '#ec4899',
] as const;
